import { ChangeSet } from '@rd-web-markets/shared/dist/models/ChangeSet';
import { hasClaimGroupUserAccessWithRoles, hasClaimGroupUserAccessWithSetting, isUserAdmin, isUserClaimGroupManager } from '@rd-web-markets/shared/dist/util/user/userUtils';
import { createPagesAbilitiesProxy } from '@rd-web-markets/shared/dist/util/userAbilities';
import { useEffect, useState } from 'react';
export { useGetUserCompanyAbilities } from '@rd-web-markets/shared/dist/util/userAbilities';
function getConsultantClaimGroupAbilities(user) {
  // true for anything not specified here
  const abilities = {
    pages: {
      ClaimGroupOverviewPage: {
        components: {
          engagementTeam: {
            canEditAnyTypeOfUser: true,
            canAddAccountants: false,
            canCreateNewCustomerUsers: false
          }
        }
      }
    }
  };
  const proxy = createPagesAbilitiesProxy(abilities);
  return proxy;
}
function getUserClaimGroupAbilities(_ref) {
  let {
    user,
    claimGroup,
    ...additionalData
  } = _ref;
  if (!['customer', 'consultant'].includes(user.account_type)) {
    return createPagesAbilitiesProxy({
      pages: {}
    });
  }
  if (user.account_type === 'consultant') {
    return getConsultantClaimGroupAbilities(user);
  }

  // by default using useParams will return string, so just in case
  const claimGroupId = parseInt(claimGroup?.id);
  if (!user || !claimGroupId) {
    return createPagesAbilitiesProxy({
      pages: {}
    });
  }
  const userClaimGroupAccess = user.user_accesses.find(user_access => user_access.rollable_id === claimGroupId && user_access.rollable_type === 'ClaimGroup') || {};
  const userIsEngagementTeamMember = userClaimGroupAccess.included_in_engagement_team;
  const userClaimGroupRoles = userClaimGroupAccess.roles || [];
  const rolesWithProjectOverviewAccess = ['technical_contact', 'financial_contact', 'project_manager'];
  const userRoleHasProjectDetailsAccess = !!rolesWithProjectOverviewAccess.filter(roleWithAccess => userClaimGroupRoles.some(userRole => roleWithAccess === userRole)).length;
  const userHasAnyClaimGroupRole = roles => roles.some(role => userClaimGroupRoles.includes(role));
  const canAccessProjectDetails = userRoleHasProjectDetailsAccess || userIsEngagementTeamMember;
  const userIsAdminOrClaimGroupManager = isUserAdmin(user) || isUserClaimGroupManager(user, claimGroup);
  const objectIsUnderClientReview = new ChangeSet({
    changeSet: additionalData?.reviewedObject?.active_change_set || {}
  }).isUnderClientReview();
  const canCompleteTrackChangesReview = userIsAdminOrClaimGroupManager || objectIsUnderClientReview;
  const canAccessViewReport = userClaimGroupAccess.access_to_signed_off_rnd_report && objectIsUnderClientReview;
  const canAccessFinancialDocuments = user.account_type !== 'customer' || userHasAnyClaimGroupRole(['client_sponsor', 'financial_contact', 'accountant']);
  const canAccessTechnicalDocuments = user.account_type !== 'customer' || userHasAnyClaimGroupRole(['client_sponsor', 'technical_contact', 'accountant']);
  const canViewClaimGroupCostDataSidebarMenu = user.account_type !== 'customer' || hasClaimGroupUserAccessWithRoles(user, claimGroup, ['financial_contact', 'client_sponsor']);
  const canSeeAddClaimSidebarButton = user.account_type !== 'customer';
  const canSeeDocumentsSidebarButton = user.account_type !== 'customer' || hasClaimGroupUserAccessWithSetting(user, claimGroup, 'has_claim_group_document_access');
  const canCreateNewProjects = user.account_type !== 'customer' || hasClaimGroupUserAccessWithSetting(user, claimGroup, 'can_create_new_projects');
  const canDownloadProjectData = user.account_type !== 'customer' || hasClaimGroupUserAccessWithSetting(user, claimGroup, 'has_data_centre_project_data_access');
  const canDownloadCostData = user.account_type !== 'customer' || hasClaimGroupUserAccessWithSetting(user, claimGroup, 'has_data_centre_cost_data_access');
  const canViewDataCentreSidebarMenu = canDownloadProjectData || canDownloadCostData;
  const abilities = {
    pages: {
      ProjectOverviewPage: {
        canAccessPage: true,
        canAccessProjectDetailsLink: canAccessProjectDetails
      },
      ClaimProjectReportDetailPage: {
        canAccessPage: canAccessProjectDetails,
        canEditProjectDetails: false,
        canEditProjectActivity: true,
        // this tab is called Project Notes (from translations) now
        canAccessProjectReportTab: canAccessProjectDetails,
        canAccessDocumentsTab: canAccessProjectDetails,
        canAccessNotesTab: canAccessProjectDetails,
        canDeleteProjectNotes: false,
        // except if own notes
        canAccessCompetentProfessionalsTab: userIsEngagementTeamMember,
        canAccessSummarySubtab: userIsEngagementTeamMember,
        canAccessTechnicalNarrativeSubtab: userIsEngagementTeamMember,
        canAccessAiDrafterSubTab: false,
        // clients cannot access this
        canAccessContactsSubTab: canAccessProjectDetails,
        canEditExistingDocumentsOrFolders: false,
        canEditContacts: false
      },
      ClaimGroupOverviewPage: {
        components: {
          engagementTeam: {
            canEditAnyTypeOfUser: false,
            canAddAccountants: userIsAdminOrClaimGroupManager,
            canCreateNewCustomerUsers: userIsAdminOrClaimGroupManager
          }
        }
      },
      ProjectReportQuestionnairesPage: {
        canEditQuestionnaire: !userIsEngagementTeamMember
      },
      ProjectRndReportsPage: {
        components: {
          ReportPreviewControls: {
            canEditDynamicFields: false
          }
        }
      },
      MyClaimGroupsPage: {
        components: {
          ClaimGroupList: {
            canAccessViewReport: canAccessViewReport
          }
        }
      },
      any: {
        canClickCompleteTrackChangesReview: canCompleteTrackChangesReview,
        canViewClaimGroupCostDataSidebarMenu,
        canSeeAddClaimSidebarButton,
        canSeeDocumentsSidebarButton,
        canViewDataCentreSidebarMenu,
        canDownloadProjectData,
        canDownloadCostData,
        canCreateNewProjects,
        canAccessFinancialDocuments,
        canAccessTechnicalDocuments,
        something: false
      }
    }
  };
  const proxy = createPagesAbilitiesProxy(abilities);
  return proxy;
}
export function getUserClaimAbilities(_ref2) {
  let {
    user,
    claim,
    ...params
  } = _ref2;
  const canDirectlyEditTotalCosts = user.account_type === 'admin';
  const abilities = {
    pages: {
      TotalCosts: {
        canDirectlyEditTotalCosts
      }
    }
  };
  const proxy = createPagesAbilitiesProxy(abilities);
  return proxy;
}
export function useGetUserClaimAbilities(_ref3) {
  let {
    user,
    claimGroup,
    ...additionalData
  } = _ref3;
  // it always initially returns this state, so we need to build it asap
  return getUserClaimAbilities({
    user,
    claimGroup,
    ...additionalData
  });
}
export function useGetUserClaimGroupAbilities(_ref4) {
  let {
    user,
    claimGroup,
    ...additionalData
  } = _ref4;
  // it always initially returns this state, so we need to build it asap
  return getUserClaimGroupAbilities({
    user,
    claimGroup,
    ...additionalData
  });
}